'use client';

import { ReactElement } from 'react';
import Alert from '../Alert';

/**
 * The heading of the notice: what the reader cannot do, stated before why.
 *
 * Follows the same shape as the check cards' own statements — the condition first, the reason
 * after — rather than opening with the explanation and leaving the consequence to be inferred.
 */
export const PERMISSION_NOTICE_TITLE = 'You cannot request checks for this customer';

/**
 * The body: the reason, and the one action that changes it.
 *
 * **Deliberately silent about which permission state this is.** "This customer has not authorised
 * your organisation" covers a customer who never granted permission and one who granted and then
 * revoked it.
 *
 * **Sometimes the signal could name which of the two it is, and this copy still does not.** When the
 * lookup answers `200`, `requestPermissionFrom` reaches `notPermitted` down two distinguishable
 * paths — no row for this organisation at all, or a newest row whose status is not `Active` — so
 * there the single sentence is a copy decision rather than a limit of the data. It stays a single
 * sentence because telling a client their authorisation "was revoked" is a claim about their
 * relationship with their customer, and which of the two a client reads is the owner's call, not
 * this component's.
 *
 * **Reached three ways, all of them an answer about permission.** Two are the `200` paths above.
 * The third is a `403`: person-api refuses this read through the same predicate that refuses the
 * `POST` behind the request control, so a `403` is that predicate speaking, not a failure to reach
 * it. In practice it is the *common* path for a client with no permission at all — such a caller
 * cannot read the permission list precisely because they hold no permission, so the `200` with an
 * empty result never arrives for them.
 *
 * **What is not reached this way** is any other failure. A `401`, a `404`, a server fault or a
 * dropped connection takes the other set below, which asserts no cause at all: nothing may name a
 * customer's authorisation on the strength of a request that did not arrive.
 */
export const PERMISSION_NOTICE_BODY =
  'This customer has not authorised your organisation. Ask them to grant permission from their ' +
  'VerifiMe wallet.';

/**
 * What the same refusal reads as when the reason is not a permission.
 *
 * **The first line is shared with the notice above, and that is the point.** Every non-`200` on
 * this path is one thing to a client — the request cannot be made — so it opens the same way.
 * Settled 2026-08-26 by the owner: "we cannot make this request succeed, so it should use the same
 * solution".
 *
 * **The second line is not shared, and that is also the point.** A `401`, a `404` or a server fault
 * carries no remedy anyone can name, and telling a staff member whose session has just expired to go
 * and ask their customer for authorisation sends them after the wrong remedy entirely. What those
 * failures have in common is that they pass, so the action they get is the one that ends them.
 *
 * **A `403` is not in that list**, though it once was in this file's own description of it. It
 * carries a remedy we know — the customer can grant permission — and so it takes the notice above.
 * D-20 settled that; the classification drifted from it when the gate's signal moved endpoints, and
 * was put back on the owner's reading of a live capture where a client with no permission was shown
 * "please refresh and try again" for a state refreshing cannot change.
 *
 * **Used by the card as well as the notification.** It was written for the notification
 * alone, when the card said nothing at all on a failure. The owner rejected that silence on seeing
 * it: a card offering nothing and explaining nothing is indistinguishable from a broken page. The
 * wording did not need to change to carry the second surface, because it never asserted a cause.
 */
export const REQUEST_UNAVAILABLE_TITLE = 'You cannot request checks for this customer right now';

/** @see REQUEST_UNAVAILABLE_TITLE */
export const REQUEST_UNAVAILABLE_BODY = 'Please refresh the page and try again.';

/**
 * Which of the two things the card knows.
 *
 * `notPermitted` — the lookup answered, and its answer was that this organisation holds no live
 * permission. There is a cause and a remedy, and the copy names both. A `403` counts as answering:
 * on this endpoint it is the authorisation predicate refusing, and the portal reads it as such
 * (D-50). It is in fact the usual route here — a client with no permission cannot read the
 * permission list at all, so the `200` carrying an empty result never reaches them.
 *
 * `unknown` — the lookup did not answer: a server fault, a dropped connection, a timeout, an
 * expired session. **The copy must not name a cause here.** Telling a client their customer
 * withheld authorisation, on the strength of a request that never got an answer, is a claim about
 * someone else's relationship that nobody made to us.
 */
export type TPermissionNoticeReason = 'notPermitted' | 'unknown';

export type TPermissionNoticeProps = {
  /**
   * Required rather than defaulted. A default would decide, silently and at every future call site,
   * which of two sentences a client reads about their own customer — and the wrong one of the two
   * asserts something we were not told.
   */
  reason: TPermissionNoticeReason;
  'data-testid'?: string;
};

/**
 * Why a check card is offering nothing to do: this organisation cannot request checks for this
 * customer.
 *
 * **Shown in two states, and in neither of them by its own decision.** It is rendered when the card
 * has no request control left to offer *and* nothing else on its action row either — the card that
 * still offers `VIEW EVIDENCE` or `VIEW ID PORTRAIT` drops the request control in silence instead.
 * "No request control left" covers both reasons below, and only one of them is a refusal: the other
 * is a lookup that never answered. The rule lives in the view that composes the card, because it
 * turns on an API result this package must not know about (ADR-0050). What lives here is the wording and its presentation, for the same reason
 * `requestCopy.ts` and `statements.ts` hold theirs: a call site that re-derives product copy can
 * get it wrong, and no test would catch it.
 *
 * **`severity="info"` for both reasons, but not for the same reason.** On `notPermitted` nothing
 * has failed and nothing is wrong with the customer: the organisation simply does not hold a
 * permission it needs. On `unknown` something did fail — a server fault, a dropped connection, a
 * role refusal — and `info` is still right, because the copy asks the reader to refresh and try
 * again. Colouring either as an error tells a client the product is broken and invites them to
 * report it.
 *
 * **`variant="standard"`, though the reviewed design calls this treatment "outlined".** Those are
 * two vocabularies, and only the `standard*` variants are themed here — see `standardInfo` in
 * `packages/styles/themes/default/lightTheme.ts` and its counterpart in `darkTheme.ts`.
 *
 * **`role="status"`, overriding MUI's default `role="alert"`.** The card withholds this notice
 * while the permission lookup is still in flight — `CustomerConfidenceCards` gates it on
 * `isResolving` — so it is inserted into a page the reader is already on rather than being present
 * at first render. That is what makes a live region the right mechanism, and `status` the right one
 * of the two: `alert` is assertive and would cut across whatever a screen-reader user is being read
 * at the moment the lookup lands.
 */
export default function PermissionNotice({
  reason,
  'data-testid': dataTestId,
}: Readonly<TPermissionNoticeProps>): ReactElement {
  const permitted = reason === 'notPermitted';

  return (
    <Alert
      severity="info"
      variant="standard"
      role="status"
      title={permitted ? PERMISSION_NOTICE_TITLE : REQUEST_UNAVAILABLE_TITLE}
      data-testid={dataTestId}
    >
      {permitted ? PERMISSION_NOTICE_BODY : REQUEST_UNAVAILABLE_BODY}
    </Alert>
  );
}
